import { useEffect, useState } from "react";
import { toast } from "sonner";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import {
  FollowUpFilingStep,
  getFollowUpInformClientTmMFiledPreview,
  sendFollowUpInformClientTmMFiledWhatsApp,
} from "../../api/followUpFiling";
import {
  getFollowUpDiscussionDetails,
  getFollowUpProgress,
} from "../../api/followUpDiscussion";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";

export const FOLLOW_UP_INFORM_CLIENT_TM_M_FILED_FORM_ID =
  "follow-up-inform-client-tm-m-filed-form";

type FollowUpInformClientTmMFiledViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function FollowUpInformClientTmMFiledView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: FollowUpInformClientTmMFiledViewProps) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [messagePreview, setMessagePreview] = useState("");
  const [applicationCount, setApplicationCount] = useState(0);
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  useEffect(() => {
    onSentStatusChange?.(hasSentWhatsapp);
  }, [hasSentWhatsapp, onSentStatusChange]);

  // Load lead phone number for display.
  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getFollowUpDiscussionDetails(leadId)
      .then((data) => {
        if (cancelled) return;
        if (data.preliminary?.phone) setPhoneNumber(data.preliminary.phone);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error ? err.message : "Failed to load lead details"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Read step-completion from workflow progress to choose Send vs Save labels.
  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getFollowUpProgress(leadId);
        const step = progress.steps.find(
          (s) => s.slug === FollowUpFilingStep.FU_S2_04_InformClientTmMFiled
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
        }
      } catch {
        if (!cancelled) setHasSentWhatsapp(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Load message preview from backend so the displayed text always matches what will be sent.
  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setPreviewLoading(true);
    getFollowUpInformClientTmMFiledPreview(leadId)
      .then((res) => {
        if (cancelled) return;
        setMessagePreview(res.message ?? "");
        setApplicationCount(res.applicationCount ?? 0);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to load message preview"
        );
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const canSend = applicationCount > 0 && messagePreview.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;
    if (hasSentWhatsapp) {
      // Already sent: just advance to the next main step.
      onSuccess?.(leadId);
      return;
    }
    if (!canSend) {
      toast.error(
        "File TM-M (set Date of Filing TM-M) for at least one application first."
      );
      return;
    }
    setConfirmMode("send");
    setShowConfirmModal(true);
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSending(true);
    (async () => {
      try {
        await sendFollowUpInformClientTmMFiledWhatsApp(leadId);
        setHasSentWhatsapp(true);
        toast.success(
          confirmMode === "resend"
            ? "Message resent to client successfully."
            : "Message sent to client successfully."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      } finally {
        setSending(false);
      }
    })();
  };

  const handleResend = async () => {
    if (!leadId || sending) return;
    if (!canSend) {
      toast.error(
        "File TM-M (set Date of Filing TM-M) for at least one application first."
      );
      return;
    }
    setConfirmMode("resend");
    setShowConfirmModal(true);
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id={FOLLOW_UP_INFORM_CLIENT_TM_M_FILED_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Message Preview */}
      <div className="flex flex-col gap-5">
        <p className="text-[16px] leading-5 text-[#222]">Message Preview</p>
        <div className="rounded-[6px] border border-[#EEEEEE] bg-white p-[14px] min-h-[148px]">
          {previewLoading && !messagePreview ? (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Loading preview...
            </p>
          ) : messagePreview ? (
            <pre
              className="font-sans text-[12px] leading-5 text-[#222] m-0 whitespace-pre-wrap break-words"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              {renderWhatsAppFormatting(messagePreview)}
            </pre>
          ) : (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              File TM-M for at least one application to generate the message.
            </p>
          )}
        </div>
      </div>

      {/* Phone number (read-only) + optional Resend button */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSentWhatsapp && (
          <button
            type="button"
            disabled={sending || !canSend}
            onClick={handleResend}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Resend Message"}
          </button>
        )}
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={messagePreview}
        onConfirm={handleConfirmProceed}
        disableConfirm={!canSend}
      />
    </form>
  );
}
